import { Command as CommandPrimitive } from 'cmdk'
import { Check, Search } from 'lucide-react'
import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { ScrollArea } from '../scroll-area'

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={twMerge(
        'flex size-full flex-col overflow-hidden rounded-md bg-white text-sm',
        className,
      )}
      {...props}
    />
  )
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className="border-monochromatic-300 flex h-9 items-center gap-2 border-b px-3"
    >
      <Search className="text-monochromatic-300 shrink-0" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={twMerge(
          'disabled:text-monochromatic-400 flex-1 border-0 bg-transparent p-0 focus-visible:outline-hidden disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      />
    </div>
  )
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <ScrollArea>
      <CommandPrimitive.List
        data-slot="command-list"
        className={twMerge('size-full max-h-52', className)}
        {...props}
      />
    </ScrollArea>
  )
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  )
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={twMerge(
        '**:[[cmdk-group-heading]]:text-monochromatic-400 overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  )
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={twMerge(
        'bg-border bg-monochromatic-300 -mx-1 h-px',
        className,
      )}
      {...props}
    />
  )
}

function CommandItem({
  className,
  children,
  isSelected = false,
  datatestid,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item> & {
  isSelected?: boolean
  datatestid?: string
}) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={twMerge(
        "data-[selected=true]:bg-bios-primary/20 relative flex cursor-default items-center gap-2 rounded-lg px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
      data-testid={`${datatestid}-${props.key}`}
    >
      <Check
        className={twMerge(isSelected ? 'opacity-100' : 'opacity-0')}
        data-testid={`${datatestid}-check-${props.key}`}
      />
      {children}
    </CommandPrimitive.Item>
  )
}

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
}
